import { TextAnimation, TextLine, TextSegment, WordToken } from './type';

/**
 * Parses a text string and extracts highlighted segments.
 * Words wrapped in backslashes (e.g. `\word\`) are treated as highlighted.
 */
export const parseHighlightedText = (text: string): TextSegment[] => {
  const parts = text.split('\\');
  const segments: TextSegment[] = [];

  parts.forEach((part, index) => {
    if (part === '') return;

    // Odd-indexed parts (between backslashes) are highlighted
    const isHighlighted = index % 2 === 1;

    segments.push({
      text: part,
      ...(isHighlighted && { highlighted: true }),
    });
  });

  return segments;
};

export const splitSegmentsIntoWords = (
  segments: TextSegment[],
): WordToken[] => {
  const words: WordToken[] = [];

  segments.forEach((segment, segIdx) => {
    const segmentWords = segment.text.split(/(\s+)/);

    segmentWords.forEach((w) => {
      if (w === '') return;

      words.push({
        word: w,
        highlighted: segment.highlighted ?? false,
        segmentIndex: segIdx,
      });
    });
  });

  return words;
};

/** Counts visible (non-whitespace) words across all segments of a line. */
const countLineWords = (line: TextLine): number =>
  line.segments
    .map((s) => s.text)
    .join('')
    .split(/\s+/)
    .filter((w) => w.length > 0).length;

export const calculateLineDelays = ({
  textLines,
  animationDelay,
  textAnimation,
  lastLineExtraDelay,
}: {
  textLines: TextLine[];
  animationDelay: number;
  textAnimation: TextAnimation;
  lastLineExtraDelay: number;
}): number[] => {
  return textLines.reduce<number[]>((delays, _line, index) => {
    if (index === 0) {
      delays.push(textAnimation.FIRST_LINE_DELAY + animationDelay);
      return delays;
    }

    const prevDelay = delays[index - 1] ?? 0;
    const prevLine = textLines[index - 1];
    const prevWordCount = prevLine ? countLineWords(prevLine) : 0;
    const isLastLine = index === textLines.length - 1;

    if (isLastLine) {
      const prevLineFullDuration =
        Math.max(0, prevWordCount - 1) * textAnimation.FRAMES_PER_WORD +
        textAnimation.WORD_FADE_DURATION;
      delays.push(prevDelay + prevLineFullDuration + lastLineExtraDelay);
      return delays;
    }

    const prevLineDuration =
      Math.max(0, prevWordCount - 1) * textAnimation.FRAMES_PER_WORD +
      textAnimation.LINE_OVERLAP_OFFSET;
    delays.push(prevDelay + prevLineDuration);
    return delays;
  }, []);
};

/**
 * Returns the frame when the side-line decoration starts, right after the last text line finishes fading in.
 */
export const calculateSideLineStart = ({
  textLines,
  lineDelays,
  framesPerWord,
  slowWordFadeDuration,
}: {
  textLines: TextLine[];
  lineDelays: number[];
  framesPerWord: number;
  slowWordFadeDuration: number;
}): number => {
  // Find the last line and its scheduled start delay
  const lastLineIndex = textLines.length - 1;
  const lastLineDelay = lineDelays[lastLineIndex] ?? 0;

  // Count how many words the last line contains
  const lastLineWordCount = textLines[lastLineIndex]
    ? countLineWords(textLines[lastLineIndex])
    : 0;

  // Total time for all words in the last line to finish fading in
  const lastLineFadeDuration =
    Math.max(0, lastLineWordCount - 1) * framesPerWord + slowWordFadeDuration;

  return lastLineDelay + lastLineFadeDuration;
};
